import * as signalR from '@microsoft/signalr';
import { HubConnectionState } from '@microsoft/signalr';
import { UUID } from 'angular2-uuid';
import moment from 'moment';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { InjectorHelper } from '../helpers/injector.helper';
import { NotificationLevel } from '../models/enums/notificationlevel.enum';
import { SettingsService } from './settings.service';
import { StandardRequestBase } from './request-base';
import { UsersService } from './users.service';

export interface IWebsocketService {
    Initialize(url: string);
    Start();
    Stop();
    Subscribe(channel: string): Promise<void>;
    Unsubscribe(channel: string);
}

export class WebsocketService {
    public static MessageReceived: Subject<any> = new Subject();
    public static Active: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private static Service: IWebsocketService;

    public static Initialize(url, useWorker) {
        if (useWorker == false) {
            WebsocketService.Service = new DirectWebSocket();
        } else {
            WebsocketService.Service = new WebWorkerWebSocket();
        }
        WebsocketService.Service.Initialize(url);
    }

    public static Start() {
        WebsocketService.Service.Start();
    }

    public static Stop() {
        WebsocketService.Service.Stop();
    }

    public static Subscribe(channel) {
        return WebsocketService.Service.Subscribe(channel);
    }
    public static Unsubscribe(channel) {
        WebsocketService.Service.Unsubscribe(channel);
    }

    private static Auffrischung: Promise<void> = null;

    /**
     * Sorgt vor einem Wiederverbinden fuer ein gueltiges Token.
     *
     * Der Client erneuert sein Token sonst nur nebenbei, wenn ein HTTP-Aufruf ansteht. Eine
     * bestehende SignalR-Verbindung prueft es nie wieder -- ein Tab hoert also stundenlang mit
     * einem laengst abgelaufenen Token mit. Startet der Notification-Dienst neu, verbindet er sich
     * genau damit, der Server lehnt ab, und ohne Benutzeraktion blieb es dabei: keine Meldungen mehr.
     *
     * Erneuert wird ueber einen gewoehnlichen Aufruf durch den Interceptor, nicht direkt: Der hat
     * die Sperre gegen doppeltes Erneuern, und ein Refresh-Token zweimal zu benutzen wertet
     * GACentral als Wiederverwendung und beendet alle Sitzungen des Benutzers.
     *
     * Aber nur, wenn das Backend erreichbar ist. Der Interceptor wertet jeden Fehler beim Erneuern
     * als abgelaufene Sitzung, auch einen Netzfehler, und meldet ab. Faellt die Verbindung weg,
     * weil das Netz oder die ganze Instanz weg ist, wuerde eine Auffrischung genau dann scheitern
     * und den Benutzer abmelden -- nachgestellt: offline, Token in den letzten fuenf Minuten,
     * Ergebnis Abmeldung und Fehlerseite. Jeder weitere gescheiterte Verbindungsversuch fragt
     * erneut, also wird erneuert, sobald das Backend wieder antwortet.
     */
    public static TokenAuffrischen(): Promise<void> {
        const expiresat = localStorage.getItem('expiresat');
        if (!localStorage.getItem('token') || !expiresat || moment(expiresat) > moment().add(5, 'm')) {
            return Promise.resolve();
        }
        if (!WebsocketService.Auffrischung) {
            const users = InjectorHelper.InjectorInstance?.get<UsersService>(UsersService);
            if (!users) {
                return Promise.resolve();
            }
            WebsocketService.Auffrischung = WebsocketService.BackendErreichbar().then(erreichbar => {
                if (!erreichbar) {
                    return;
                }
                return new Promise<void>(resolve => {
                    users.GetCurrentUser().subscribe({ next: () => resolve(), error: () => resolve() });
                });
            }).finally(() => WebsocketService.Auffrischung = null);
        }
        return WebsocketService.Auffrischung;
    }

    /** Antwortet das Backend? Anonym und am Interceptor vorbei, ein Fehler hier aendert nichts. */
    private static async BackendErreichbar(): Promise<boolean> {
        const abbruch = new AbortController();
        const frist = setTimeout(() => abbruch.abort(), 5000);
        try {
            const antwort = await fetch(SettingsService.API_BASE_URL.getValue() + 'api/health', { cache: 'no-store', signal: abbruch.signal });
            return antwort.ok;
        } catch {
            return false;
        } finally {
            clearTimeout(frist);
        }
    }

    public static WaitForWebSocketService() {
        return new Promise<void>(resolve => {
            const active = WebsocketService.Active.getValue();
            if (active) {
                resolve();
            } else {
                const sub = WebsocketService.Active.subscribe(x => {
                    if (x) {
                        sub.unsubscribe();
                        resolve();
                    }
                });
            }
        });
    }
}

export class WebsocketSubscription {
    private Subscription: Subscription;
    private Active = true;    

    static SendNotification(channel: string, type: string, content: any) {
        if (content) {
            const user = UsersService.ActiveUser.getValue();
            if (user) {
                const service = InjectorHelper.InjectorInstance.get<StandardRequestBase>(StandardRequestBase);
                service.executePost({
                    Channels: [channel],
                    Type: type,
                    Content: JSON.stringify(content)
                }, 'config/api/notification', 'SendNotification').subscribe();
            }
        }
    }

    constructor(private Channel: string, type: string, handler) {
        WebsocketService.WaitForWebSocketService().then(() => {
            if (this.Active) {
                const sub = WebsocketService.MessageReceived.subscribe((response) => {
                    const parsed = JSON.parse(response);
                    if (parsed.Level === NotificationLevel.System &&
                        parsed.Channels && parsed.Channels.some(x => x === this.Channel) &&
                        parsed.Type === type) {
                        handler(parsed.Content ?? '');
                    }
                });
                WebsocketService.Subscribe([{ Channel: this.Channel, Level: [0] }]).then(() => {
                    this.Subscription = sub;
                    if (!this.Active) {
                        this.Unsubscribe();
                    }
                });
            }
        });
    }

    Unsubscribe() {
        this.Active = false;
        if (this.Subscription) {
            this.Subscription.unsubscribe();
            WebsocketService.Unsubscribe([{ Channel: this.Channel, Level: [0] }]);
        }
    }
}

export class WebWorkerWebSocket implements IWebsocketService {
    private Subscriptions = {};
    private Worker: Worker;

    Initialize(url) {
        const WORKER_ENABLED = !!(Worker);

        if (WORKER_ENABLED) {

            this.Worker = new Worker('assets/signalr/websocket.js');

            this.Worker.onmessage = (data) => {
                const dataObj = data.data;
                switch (dataObj.Type) {
                    case 'Active':
                        WebsocketService.Active.next(dataObj.Value);
                        if (!dataObj.Value) {
                            // Verbindung weg: Token auffrischen, falls noetig. Das neue Token geht
                            // ueber TokenSet an den Worker, und der verbindet sich dann sofort.
                            WebsocketService.TokenAuffrischen();
                        }
                        break;
                    case 'Message':
                        WebsocketService.MessageReceived.next(dataObj.Value);
                        break;
                    case 'Subscription':
                        const resolve = this.Subscriptions[dataObj.Value];
                        if (resolve) {
                            resolve();
                        }
                        break;
                }
            };

            this.Worker.onerror = (data) => {
                console.log(data);
            };

            this.SendMessage('Initialize', {
                URL: url,
                Token: localStorage.getItem('token')
            });
            UsersService.TokenSet.subscribe(() => {
                this.SendMessage('Token', localStorage.getItem('token'));
            });
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    this.SendMessage('Check', '');
                }
            });
        } else {
            throw new Error('WebWorker is not enabled');
        }
    }

    Start() {
        this.SendMessage('Start', '');
    }

    Stop() {
        this.SendMessage('Stop', '');
    }

    Subscribe(channel) {
        return new Promise<void>(resolve => {
            const data = {
                Channel: channel,
                ID: UUID.UUID()
            };
            this.Subscriptions[data.ID] = resolve;
            this.SendMessage('Subscribe', data);
        });
    }

    Unsubscribe(channel) {
        this.SendMessage('UnSubscribe', channel);
    }

    private SendMessage(type: string, data) {
        if (this.Worker) {
            this.Worker.postMessage({
                Type: type,
                Value: JSON.parse(JSON.stringify(data))
            });
        }
    }
}

export class DirectWebSocket implements IWebsocketService {

    Connected: boolean = false;
    private URL: string = '';
    private hubConnection: signalR.HubConnection
    private ManualClose = false;

    public Initialize(url) {
        this.URL = url;
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && !this.Verbunden()) {
                this.Jetzt();
            }
        });
        // Neues Token und keine Verbindung: sofort verbinden statt auf den naechsten Versuch zu
        // warten -- der kann bis zu 30 Sekunden entfernt sein, und was in der Zeit gemeldet
        // wird, geht verloren.
        UsersService.TokenSet.subscribe(() => {
            if (!this.ManualClose && !this.Verbunden()) {
                this.Jetzt();
            }
        });
    }
    /** Verbunden oder gerade dabei -- dann ist kein weiterer Aufbau noetig. */
    private Verbunden() {
        return this.hubConnection && this.hubConnection.state !== HubConnectionState.Disconnected;
    }
    private Ausstehend: any = null;
    /** Einen geplanten Versuch vorziehen, ohne einen zweiten daneben zu starten. */
    private Jetzt() {
        if (this.Ausstehend) {
            clearTimeout(this.Ausstehend);
            this.Ausstehend = null;
        }
        this.Try = 0;
        this.Start();
    }
    private Try = 0;
    /**
     * Wachsender Abstand, der letzte Wert gilt danach endlos. Frueher stand hier
     * [0, 1000, 2000, 30000, 10000, null]: Nach fuenf Versuchen war der Eintrag null, der Client
     * setzte ManualClose und gab endgueltig auf -- er schwieg dann bis zum naechsten Tabwechsel,
     * und es sah aus, als liefen die Workflows nicht mehr.
     */
    private Timeouts = [0, 1000, 2000, 5000, 10000, 30000];
    /**
     * Abonnierte Kanaele. Der Hub haengt sie an die Verbindungs-ID und vergisst sie beim Abbruch;
     * ohne erneute Anmeldung ist der Client nach einer Wiederverbindung zwar verbunden, hoert aber
     * nichts mehr.
     */
    private Channels = [];
    public Start() {
            if (this.Verbunden()) {
                return;
            }
            if (localStorage.getItem('token')) {
                this.ManualClose = false;
                this.hubConnection = new signalR.HubConnectionBuilder().withUrl(this.URL, {
                    skipNegotiation: true,
                    transport: signalR.HttpTransportType.WebSockets,
                    accessTokenFactory: () => {
                        return window.localStorage.getItem('token');
                    }
                }).configureLogging(signalR.LogLevel.None).build();

                this.hubConnection.onclose(() => {
                    if (!this.ManualClose) {
                        if (this.GetrenntSeit === null) {
                            this.GetrenntSeit = Date.now();
                        }
                        this.Reconnect();
                    }
                });

                this.hubConnection
                    .start()
                    .then(async () => {
                        await this.until(() => {
                            return this.hubConnection.state == signalR.HubConnectionState.Connected;
                        });
                        this.Try = 0;
                        this.Connected = true;
                        this.AddMessageListener();
                        this.AddInternalMessageListener();
                        await this.Resubscribe();
                        WebsocketService.Active.next(true);
                        await this.NachVerbindung();
                    })
                    .catch(err => {
                        this.Reconnect();
                    })
            } else {
                WebsocketService.Active.next(false);
            }
    }
    public Stop() {
        if (this.hubConnection) {
            WebsocketService.Active.next(false);
            this.ManualClose = true;
            // Nach einer Abmeldung nichts nachholen: Das waeren Meldungen fuer die alte Sitzung.
            this.GetrenntSeit = null;
            this.hubConnection.stop();
        }
    }
    private Reconnect() {
        this.Connected = false;
        WebsocketService.Active.next(false);
        if (localStorage.getItem('token') && !this.ManualClose) {
            // Mit abgelaufenem Token wuerde jeder weitere Versuch abgelehnt. Kommt ein neues,
            // verbindet TokenSet sofort.
            WebsocketService.TokenAuffrischen();
            const timeout = this.Timeouts[Math.min(this.Try, this.Timeouts.length - 1)];
            if (this.Ausstehend) {
                clearTimeout(this.Ausstehend);
            }
            this.Ausstehend = setTimeout(() => {
                this.Ausstehend = null;
                this.Try += 1;
                this.Start();
            }, timeout);
        }
    }
    private AddMessageListener() {
        this.hubConnection.on('message', (message) => this.Weitergeben(message));
    }
    private AddInternalMessageListener() {
        this.hubConnection.on('internal', (message) => this.Weitergeben(message));
    }

    /**
     * Nachholen nach einer Unterbrechung (NotificationService/Nachrichtenpuffer): Der Dienst haelt
     * verschickte Meldungen drei Minuten fest. Nach einer Wiederverbindung holt der Client, was seit
     * der Trennung kam -- vor allem nach einem Neustart des Dienstes ging das bisher verloren.
     */
    private GetrenntSeit: number = null;
    private Gesehen: string[] = [];

    /** Kurzer Fingerabdruck einer Meldung, um am Rand doppelt Geliefertes zu erkennen. */
    private Fingerabdruck(text: string) {
        let h = 5381;
        for (let i = 0; i < text.length; i++) {
            h = ((h << 5) + h + text.charCodeAt(i)) | 0;
        }
        return text.length + ':' + h;
    }

    private Weitergeben(message: string) {
        const fp = this.Fingerabdruck(message || '');
        if (this.Gesehen.indexOf(fp) >= 0) {
            return;
        }
        this.Gesehen.push(fp);
        if (this.Gesehen.length > 300) {
            this.Gesehen.shift();
        }
        WebsocketService.MessageReceived.next(message);
    }

    /** Uhrenversatz bestimmen und nachholen. Kennt der Dienst die Methoden nicht (aelteres Image), passiert nichts. */
    private async NachVerbindung() {
        let versatz: number;
        try {
            const server = await this.hubConnection.invoke<string>('Serverzeit');
            versatz = Date.parse(server) - Date.now();
        } catch {
            this.GetrenntSeit = null;
            return;
        }
        if (this.GetrenntSeit === null) {
            return;
        }
        // Fuenf Sekunden Rand: lieber eine Meldung doppelt (wird verworfen) als eine verloren.
        const seit = new Date(this.GetrenntSeit + versatz - 5000).toISOString();
        this.GetrenntSeit = null;
        const kanaele: string[] = [];
        this.Channels.forEach(c => (Array.isArray(c) ? c : [c]).forEach(x => kanaele.push(x.Channel)));
        try {
            const nachgeholt = await this.hubConnection.invoke<any[]>('Nachholen', kanaele, seit);
            (nachgeholt || []).forEach(m => this.Weitergeben(m.inhalt !== undefined ? m.inhalt : m.Inhalt));
        } catch {
            // Nachholen ist eine Ergaenzung; scheitert es, bleibt es beim bisherigen Verhalten.
        }
    }
    public Subscribe(channel) {
        const key = JSON.stringify(channel);
        if (!this.Channels.some(x => JSON.stringify(x) === key)) {
            this.Channels.push(channel);
        }
        return this.hubConnection.invoke('Subscribe', channel);
    }
    public Unsubscribe(channel) {
        const key = JSON.stringify(channel);
        this.Channels = this.Channels.filter(x => JSON.stringify(x) !== key);
        this.hubConnection.invoke('UnSubscribe', channel);
    }

    /**
     * Nach jeder Wiederverbindung die Kanaele erneut anmelden. Wartet, bis alle durch sind -- erst
     * danach wird nachgeholt, sonst bliebe zwischen Anmelden und Nachholen eine Luecke.
     */
    private Resubscribe() {
        return Promise.all(this.Channels.map(channel =>
            this.hubConnection.invoke('Subscribe', channel).catch(() => {
                // Eine fehlgeschlagene Anmeldung darf die uebrigen nicht verhindern.
            })));
    }

    private sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private async until(fn) {
        while (!fn()) {
            await this.sleep(0)
        }
    }
}